import { ref, onMounted, onUnmounted, readonly } from 'vue'
import { useNotifications } from './useNotifications'
import { useApi } from './useApi'
import { useAuth } from './useAuth'

// Function to get the correct API base URL
const getApiBaseUrl = (): string => {
  // Check if we're running on localhost (development)
  // if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  //   return '' // Use relative URLs that will be proxied by Vite
  // }
  // Production: use the deployed Cloudflare Worker URL
  return 'https://delivery-area.mwsemple.workers.dev'
}

export function useNetworkStatus() {
  const isOnline = ref(navigator.onLine)
  const isConnecting = ref(false)
  const { showError, showSuccess, showWarning } = useNotifications()
  const { get } = useApi()
  const { authState } = useAuth()

  let reconnectAttempts = 0
  let reconnectTimer: number | null = null
  const maxReconnectAttempts = 5

  const handleOnline = () => {
    if (!isOnline.value) {
      isOnline.value = true
      reconnectAttempts = 0
      
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      
      showSuccess('Connection restored', 'You are back online!')
    }
  }

  const handleOffline = () => {
    isOnline.value = false
    showError('Connection lost', 'You appear to be offline. Some features may not work.', true)
    
    // Start attempting to reconnect
    attemptReconnect()
  }

  const attemptReconnect = () => {
    if (reconnectAttempts >= maxReconnectAttempts) {
      showError('Unable to reconnect', 'Please check your internet connection and refresh the page.', true)
      return
    }

    reconnectAttempts++
    isConnecting.value = true

    // Try to make a simple request to check connectivity
    const timeoutId = setTimeout(async () => {
      try {
        // Only attempt health check if user is authenticated and has required scope
        if (authState.value.isAuthenticated && authState.value.hasRequiredScope) {
          await get('/health')
          handleOnline()
        } else {
          // If not authenticated, treat as offline for now
          scheduleNextReconnect()
        }
      } catch (error) {
        scheduleNextReconnect()
      } finally {
        isConnecting.value = false
      }
    }, 1000 * reconnectAttempts) // Exponential backoff

    reconnectTimer = timeoutId
  }

  const scheduleNextReconnect = () => {
    if (reconnectAttempts < maxReconnectAttempts) {
      showWarning(`Reconnection attempt ${reconnectAttempts}/${maxReconnectAttempts} failed`, 'Trying again...')
      
      reconnectTimer = setTimeout(() => {
        attemptReconnect()
      }, 2000 * reconnectAttempts) // Exponential backoff: 2s, 4s, 6s, 8s, 10s
    }
  }

  const checkConnection = async (): Promise<boolean> => {
    try {
      // Only attempt health check if user is authenticated and has required scope
      if (authState.value.isAuthenticated && authState.value.hasRequiredScope) {
        await get('/health')
        return true
      } else {
        // If not authenticated, consider connection as unavailable
        return false
      }
    } catch {
      return false
    }
  }

  const forceReconnect = () => {
    reconnectAttempts = 0
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    attemptReconnect()
  }

  onMounted(() => {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    // Initial connection check - only if authenticated
    if (navigator.onLine && authState.value.isAuthenticated && authState.value.hasRequiredScope) {
      checkConnection().then(connected => {
        if (!connected) {
          handleOffline()
        }
      })
    }
  })

  onUnmounted(() => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
    }
  })

  return {
    isOnline: readonly(isOnline),
    isConnecting: readonly(isConnecting),
    checkConnection,
    forceReconnect,
  }
} 